import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Pencil, Trash2, Repeat, Download, Users, Building, Zap, Wifi, Wallet, Car, Fuel, CreditCard, Monitor, Briefcase, Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Check, RefreshCw, Undo2, HandCoins, ArrowDownRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  getFixedExpenses,
  createFixedExpense,
  updateFixedExpense,
  deleteFixedExpense,
  getExpenseTransactions,
  generateMonthlyTransactions,
  markTransactionPaid,
  revertToPending,
  formatCurrency,
  handleNumericInput,
  parseNumericValue,
  formatIndianNumber,
  getBankAccounts,
  getStatutoryMonthly,
  getEmployeeAdvances,
  createEmployeeAdvance,
  deleteEmployeeAdvance,
  recordAdvanceRecovery,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";
import { ProjectAllocation } from "../components/ProjectAllocation";
import GSTInput from "../components/GSTInput";
import { DatePicker } from "../components/DatePicker";

const emptyExpense = {
  company: "empact-studio",
  name: "",
  category: "salary",
  standard_amount: "",
  frequency: "monthly",
  employee_name: "",
  card_name: "",
  description: "",
  other_category: "",
  is_active: true,
  base_amount: "",
  cgst_rate: "",
  sgst_rate: "",
  igst_rate: "",
  tds_rate: "0",
};

const categories = [
  { value: "salary", label: "Salary", icon: Users },
  { value: "rent", label: "Rent", icon: Building },
  { value: "office-electricity", label: "Office Electricity", icon: Zap },
  { value: "kavi-electricity", label: "Kavi Electricity", icon: Zap },
  { value: "communication", label: "Communication", icon: Wifi },
  { value: "cash-withdrawals", label: "Cash Withdrawals", icon: Wallet },
  { value: "car-maintenance", label: "Car Maintenance", icon: Car },
  { value: "fuel", label: "Fuel", icon: Fuel },
  { value: "club-expenses", label: "Club Expenses", icon: CreditCard },
  { value: "credit-card", label: "Credit Card Expenses", icon: CreditCard },
  { value: "office-expenses", label: "Office Expenses", icon: Briefcase },
  { value: "software", label: "Software Expenses", icon: Monitor },
  { value: "professional-fees", label: "Professional Fees", icon: Briefcase },
  { value: "other", label: "Other", icon: Repeat },
];

const categoryColors = {
  salary: "bg-blue-100 text-blue-600",
  communication: "bg-cyan-100 text-cyan-600",
  "cash-withdrawals": "bg-green-100 text-green-600",
  "car-maintenance": "bg-orange-100 text-orange-600",
  fuel: "bg-amber-100 text-amber-600",
  "office-electricity": "bg-yellow-100 text-yellow-600",
  "kavi-electricity": "bg-lime-100 text-lime-600",
  "club-expenses": "bg-pink-100 text-pink-600",
  "credit-card": "bg-red-100 text-red-600",
  "office-expenses": "bg-indigo-100 text-indigo-600",
  software: "bg-violet-100 text-violet-600",
  "professional-fees": "bg-purple-100 text-purple-600",
  rent: "bg-slate-100 text-slate-600",
  other: "bg-gray-100 text-gray-600",
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  for (let i = -12; i <= 12; i++) {
    const date = new Date(currentYear, currentMonth + i, 1);
    const year = date.getFullYear();
    const month = date.getMonth();
    options.push({
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[month]} ${year}`,
      year,
      month
    });
  }
  return options;
};

export default function FixedExpenses() {
  const { canEdit } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState(emptyExpense);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [editedAmounts, setEditedAmounts] = useState({});
  const [editedExpenseAmounts, setEditedExpenseAmounts] = useState({}); // For expenses without transactions
  const [savingId, setSavingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null); // { type: 'transaction' | 'expense', item: obj }
  const [payDialogAllocations, setPayDialogAllocations] = useState([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
  const [payDialogBankId, setPayDialogBankId] = useState("");
  const [payDate, setPayDate] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [tdsPaidMonths, setTdsPaidMonths] = useState(new Set());
  const [advances, setAdvances] = useState([]);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ fixed_expense_id: "", employee_name: "", company: "", type: "advance", amount: "", monthly_emi: "", bank_account_id: "", notes: "" });
  const [recoveryDialog, setRecoveryDialog] = useState(null);
  const [recoveryAmount, setRecoveryAmount] = useState("");
  
  const currentMonthValue = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const fetchData = useCallback(async (autoGenerate = false) => {
    try {
      setLoading(true);
      const [expensesRes, transactionsRes] = await Promise.all([
        getFixedExpenses(selectedMonth),
        getExpenseTransactions(selectedMonth)
      ]);
      setExpenses(expensesRes.data);
      setTransactions(transactionsRes.data);
      
      // Auto-generate transactions if none exist for this month
      if (autoGenerate && transactionsRes.data.length === 0 && expensesRes.data.length > 0) {
        await handleGenerateMonth(true); // silent mode
      }
    } catch (error) {
      toast.error("Failed to load fixed expenses");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData(true); // Auto-generate on first load
    Promise.all([
      getBankAccounts(),
      getStatutoryMonthly("all"),
      getEmployeeAdvances("active"),
    ]).then(([bankRes, statRes, advRes]) => {
      setBankAccounts(bankRes.data);
      const paidSet = new Set();
      (statRes.data?.tds || []).forEach(t => { if (t.status === "paid") paidSet.add(t.month); });
      setTdsPaidMonths(paidSet);
      setAdvances(advRes.data);
    }).catch(() => {});
  }, [selectedMonth, fetchData]);

  const handleGenerateMonth = async (silent = false) => {
    try {
      setGenerating(true);
      const response = await generateMonthlyTransactions(selectedMonth);
      if (response.data.generated) {
        if (!silent) {
          toast.success(`Generated ${response.data.count} transactions for ${selectedMonth}`);
        }
        // Refetch to get the new transactions
        const transactionsRes = await getExpenseTransactions(selectedMonth);
        setTransactions(transactionsRes.data);
      } else if (!silent) {
        toast.info(response.data.message);
      }
    } catch (error) {
      if (!silent) {
        toast.error("Failed to generate transactions");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      const amount = expense.standard_amount !== undefined ? expense.standard_amount : expense.amount;
      setFormData({
        company: expense.company || "empact-studio",
        name: expense.name,
        category: expense.category,
        standard_amount: amount,
        frequency: expense.frequency,
        employee_name: expense.employee_name || "",
        card_name: expense.card_name || "",
        description: expense.description || "",
        other_category: expense.category === "other" ? (expense.description || "") : "",
        is_active: expense.is_active !== false,
        base_amount: expense.base_amount || amount || "",
        cgst_rate: expense.cgst_rate || "",
        sgst_rate: expense.sgst_rate || "",
        igst_rate: expense.igst_rate || "",
        tds_rate: expense.tds_rate?.toString() || "0",
      });
    } else {
      setSelectedExpense(null);
      setFormData({...emptyExpense, company: filterCompany !== "all" ? filterCompany : "empact-studio"});
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedExpense(null);
    setFormData(emptyExpense);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const baseAmt = parseNumericValue(formData.base_amount || formData.standard_amount);
      const cgst = parseNumericValue(formData.cgst_rate || "0");
      const sgst = parseNumericValue(formData.sgst_rate || "0");
      const igst = parseNumericValue(formData.igst_rate || "0");
      const tds = parseNumericValue(formData.tds_rate || "0");
      const gstAmount = baseAmt * (cgst + sgst + igst) / 100;
      const tdsAmount = baseAmt * tds / 100;
      const netAmount = baseAmt + gstAmount - tdsAmount;

      const submitData = {
        ...formData,
        base_amount: baseAmt,
        standard_amount: Math.round(netAmount * 100) / 100,
        cgst_rate: cgst,
        sgst_rate: sgst,
        igst_rate: igst,
        tds_rate: tds,
        employee_name: formData.employee_name || null,
        card_name: formData.card_name || null,
        description: formData.category === "other" 
          ? (formData.other_category || formData.description || null)
          : (formData.description || null),
      };
      delete submitData.other_category;
      
      if (selectedExpense) {
        await updateFixedExpense(selectedExpense.id, { ...submitData, effective_from: selectedMonth });
        toast.success(`Fixed expense updated (effective from ${selectedMonthLabel})`);
      } else {
        await createFixedExpense({ ...submitData, active_from: selectedMonth });
        toast.success(`Fixed expense added from ${selectedMonthLabel}`);
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error("Failed to save fixed expense");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFixedExpense(selectedExpense.id, selectedMonth);
      toast.success(`"${selectedExpense.name}" will stop showing from ${selectedMonthLabel} onwards`);
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate fixed expense");
    }
  };

  const handleAmountChange = (transactionId, value) => {
    setEditedAmounts(prev => ({
      ...prev,
      [transactionId]: handleNumericInput(value)
    }));
  };

  // Handle amount change for expenses without transactions
  const handleExpenseAmountChange = (expenseId, value) => {
    setEditedExpenseAmounts(prev => ({
      ...prev,
      [expenseId]: handleNumericInput(value)
    }));
  };

  const handleMarkPaid = async (transaction) => {
    const allocated = transaction.standard_amount || transaction.amount || 0;
    // For pending/draft, nothing has been paid yet. For partial, amount_paid is cumulative.
    const alreadyPaid = (transaction.status === "partial") ? (transaction.amount_paid || 0) : 0;
    const remaining = Math.max(0, allocated - alreadyPaid);
    // Default to remaining balance (or edited amount if user typed one)
    const editedVal = editedAmounts[transaction.id] !== undefined
      ? parseNumericValue(editedAmounts[transaction.id])
      : remaining;
    const activeAdv = getActiveAdvance(transaction.fixed_expense_id);
    const suggestedAmount = activeAdv ? Math.max(0, editedVal - activeAdv.monthly_emi) : editedVal;
    setPayDialog({ type: 'transaction', item: transaction, amount: suggestedAmount, advance: activeAdv || null, alreadyPaid });
    setPayDialogAllocations([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
    setPayDialogBankId("");
  };

  const handleConfirmFixedPayment = async (closeExpense = false) => {
    const { type, item, amount, advance } = payDialog;
    const validAllocs = payDialogAllocations.filter(a => a.project_id && a.line_item_id && parseFloat(a.amount) > 0);
    const projectAlloc = validAllocs.length > 0
      ? { project_allocations: validAllocs.map(a => ({ project_id: a.project_id, line_item_id: a.line_item_id, amount: parseFloat(a.amount) })) }
      : {};

    try {
      setSavingId(item.id);
      if (type === 'transaction') {
        await markTransactionPaid(item.id, { amount_paid: amount, notes: null, bank_account_id: payDialogBankId || null, paid_date: payDate || null, close_expense: closeExpense, ...projectAlloc });
        toast.success(closeExpense ? `"${item.name}" paid & closed` : `Marked "${item.name}" as paid`);
        setEditedAmounts(prev => { const s = { ...prev }; delete s[item.id]; return s; });
      } else {
        await generateMonthlyTransactions(selectedMonth);
        const transactionsRes = await getExpenseTransactions(selectedMonth);
        const newTransaction = transactionsRes.data.find(t => t.fixed_expense_id === item.id);
        if (newTransaction) {
          await markTransactionPaid(newTransaction.id, { amount_paid: amount, notes: null, bank_account_id: payDialogBankId || null, paid_date: payDate || null, close_expense: closeExpense, ...projectAlloc });
          toast.success(closeExpense ? `"${item.name}" paid & closed` : `Marked "${item.name}" as paid`);
          setEditedExpenseAmounts(prev => { const s = { ...prev }; delete s[item.id]; return s; });
        }
      }
      // Auto-record loan recovery if there was an active advance
      if (advance && advance.id) {
        const emiDeducted = advance.monthly_emi;
        try {
          await recordAdvanceRecovery(advance.id, { month: selectedMonth, amount: emiDeducted });
          toast.success(`Loan recovery of ${formatCurrency(emiDeducted)} recorded for ${advance.employee_name}`);
          const advRes = await getEmployeeAdvances("active");
          setAdvances(advRes.data);
        } catch (err) {
          // Recovery might fail if already settled — not critical
        }
      }
      setPayDialog(null);
      setPayDate("");
      fetchData(false);
    } catch (error) {
      toast.error("Failed to mark as paid");
    } finally {
      setSavingId(null);
    }
  };

  const handleRevertToPending = async (transaction) => {
    try {
      setSavingId(transaction.id);
      await revertToPending(transaction.id);
      toast.success(`Reverted "${transaction.name}" to pending`);
      fetchData();
    } catch (error) {
      toast.error("Failed to revert to pending");
    } finally {
      setSavingId(null);
    }
  };

  // Handle mark paid for expense without transaction (auto-generate first)
  const handleMarkPaidWithoutTransaction = async (expense) => {
    const editedAmount = editedExpenseAmounts[expense.id];
    const allocatedAmount = expense.standard_amount !== undefined ? expense.standard_amount : expense.amount;
    const amountToPay = editedAmount !== undefined ? parseNumericValue(editedAmount) : allocatedAmount;
    const activeAdv = getActiveAdvance(expense.id);
    const suggestedAmount = activeAdv ? Math.max(0, amountToPay - activeAdv.monthly_emi) : amountToPay;
    setPayDialog({ type: 'expense', item: expense, amount: suggestedAmount, advance: activeAdv || null });
    setPayDialogAllocations([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
    setPayDialogBankId("");
  };

  // ---- Employee Advances / Loans ----
  const getActiveAdvance = (expenseId) => advances.find(a => a.fixed_expense_id === expenseId && a.status === "active");

  const handleOpenAdvanceDialog = (expense) => {
    setAdvanceForm({
      fixed_expense_id: expense.id,
      employee_name: expense.employee_name || expense.name,
      company: expense.company,
      type: "advance",
      amount: "",
      monthly_emi: "",
      bank_account_id: "",
      notes: "",
    });
    setAdvanceDialogOpen(true);
  };

  const handleCreateAdvance = async () => {
    try {
      const amount = parseNumericValue(advanceForm.amount);
      if (amount <= 0) { toast.error("Enter a valid amount"); return; }
      const emi = advanceForm.type === "loan" ? parseNumericValue(advanceForm.monthly_emi) : amount;
      if (advanceForm.type === "loan" && emi <= 0) { toast.error("Enter a valid monthly EMI"); return; }
      await createEmployeeAdvance({
        ...advanceForm,
        amount,
        monthly_emi: emi,
        issued_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`${advanceForm.type === "loan" ? "Loan" : "Advance"} of ${formatCurrency(amount)} issued to ${advanceForm.employee_name}`);
      setAdvanceDialogOpen(false);
      const advRes = await getEmployeeAdvances("active");
      setAdvances(advRes.data);
      // Re-fetch bank accounts to reflect deduction
      const bankRes = await getBankAccounts();
      setBankAccounts(bankRes.data);
    } catch (error) {
      toast.error("Failed to create advance");
    }
  };

  const handleDeleteAdvance = async (advanceId) => {
    try {
      await deleteEmployeeAdvance(advanceId);
      toast.success("Advance deleted");
      const advRes = await getEmployeeAdvances("active");
      setAdvances(advRes.data);
    } catch (error) {
      toast.error("Failed to delete advance");
    }
  };

  const handleOpenRecoveryDialog = (advance) => {
    setRecoveryDialog(advance);
    setRecoveryAmount(formatIndianNumber(advance.monthly_emi));
  };

  const handleRecordRecovery = async () => {
    try {
      const amount = parseNumericValue(recoveryAmount);
      if (amount <= 0) { toast.error("Enter a valid amount"); return; }
      await recordAdvanceRecovery(recoveryDialog.id, { month: selectedMonth, amount });
      toast.success(`Recovery of ${formatCurrency(amount)} recorded`);
      setRecoveryDialog(null);
      const advRes = await getEmployeeAdvances("active");
      setAdvances(advRes.data);
    } catch (error) {
      toast.error("Failed to record recovery");
    }
  };

  const filteredExpenses = expenses.filter((exp) => {
    const categoryMatch = filterCategory === "all" || exp.category === filterCategory;
    const companyMatch = filterCompany === "all" || exp.company === filterCompany;
    return categoryMatch && companyMatch;
  });

  // Group expenses by category
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const groupedExpenses = useMemo(() => {
    const groups = {};
    filteredExpenses.forEach(exp => {
      const cat = exp.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(exp);
    });
    // Sort categories: salary first, then alphabetically
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'salary') return -1;
      if (b === 'salary') return 1;
      return a.localeCompare(b);
    });
  }, [filteredExpenses]);

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const navigateMonth = (direction) => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < monthOptions.length) {
      setSelectedMonth(monthOptions[newIndex].value);
    }
  };

  const getAmount = (exp) => exp.standard_amount !== undefined ? exp.standard_amount : (exp.amount || 0);

  // Get transaction for a master list item
  const getTransaction = (expenseId) => {
    return transactions.find(tx => tx.fixed_expense_id === expenseId);
  };

  // Calculate totals
  const totalAllocated = filteredExpenses.reduce((sum, exp) => {
    if (exp.is_active === false) return sum;
    const amount = getAmount(exp);
    if (exp.frequency === "monthly") return sum + amount;
    if (exp.frequency === "quarterly") return sum + amount / 3;
    if (exp.frequency === "yearly") return sum + amount / 12;
    return sum;
  }, 0);

  // Total paid so far (from transactions marked as paid or partial)
  const totalPaidSoFar = transactions
    .filter(tx => (tx.status === "paid" || tx.status === "partial") && (filterCompany === "all" || tx.company === filterCompany))
    .reduce((sum, tx) => sum + tx.amount_paid, 0);

  // Balance pending = Allocated - Paid So Far
  const totalBalancePending = totalAllocated - totalPaidSoFar;

  // Company totals
  const companyTotals = COMPANY_OPTIONS.reduce((acc, company) => {
    const allocated = expenses
      .filter((exp) => exp.company === company.value && exp.is_active !== false)
      .reduce((sum, exp) => {
        const amount = getAmount(exp);
        if (exp.frequency === "monthly") return sum + amount;
        if (exp.frequency === "quarterly") return sum + amount / 3;
        if (exp.frequency === "yearly") return sum + amount / 12;
        return sum;
      }, 0);
    
    const paid = transactions
      .filter(tx => tx.company === company.value && (tx.status === "paid" || tx.status === "partial"))
      .reduce((sum, tx) => sum + tx.amount_paid, 0);
    
    acc[company.value] = { allocated, paid };
    return acc;
  }, {});

  const selectedMonthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;
  const hasTransactions = transactions.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-brand-orange animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fixed-expenses-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Fixed Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">
            Allocated vs Actual for {selectedMonthLabel}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-fixed-expenses"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="add-fixed-expense-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          )}
        </div>
      </div>

      {/* Month Selector and Summary */}
      <Card className="bg-brand-navy text-white">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10 h-8 w-8"
                onClick={() => navigateMonth(-1)}
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger 
                  className="w-36 sm:w-40 bg-white/10 border-white/20 text-white"
                  data-testid="select-month"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10 h-8 w-8"
                onClick={() => navigateMonth(1)}
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>

              {!hasTransactions && (
                <Button
                  onClick={handleGenerateMonth}
                  disabled={generating}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  data-testid="generate-month-btn"
                >
                  {generating ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-1" />
                  )}
                  <span className="hidden sm:inline">Generate</span>
                </Button>
              )}
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Allocated</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums">{formatCurrency(totalAllocated)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Paid So Far</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums text-emerald-400">{formatCurrency(totalPaidSoFar)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Balance Pending</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums text-brand-orange">{formatCurrency(totalBalancePending)}</p>
              </div>
            </div>
          </div>

          {/* Company Breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 pt-4 border-t border-white/10">
            {COMPANY_OPTIONS.map((company) => (
              <div 
                key={company.value} 
                className="bg-white/10 rounded-lg p-3"
              >
                <p className="text-white/70 text-xs font-medium">{company.label}</p>
                <div className="flex justify-between items-end mt-1">
                  <div>
                    <p className="text-xs text-white/50">Allocated</p>
                    <p className="text-sm sm:text-lg font-bold tabular-nums">
                      {formatCurrency(companyTotals[company.value]?.allocated || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/50">Paid</p>
                    <p className="text-sm sm:text-lg font-bold tabular-nums text-emerald-400">
                      {formatCurrency(companyTotals[company.value]?.paid || 0)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Advances & Loans */}
      {advances.length > 0 && (
        <Card data-testid="advances-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-amber-600" />
              Active Advances & Loans
              <Badge variant="secondary" className="ml-auto text-xs">{advances.length} active</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Monthly EMI</TableHead>
                    <TableHead className="text-right">Recovered</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-center">Issued</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advances.map((adv) => (
                    <TableRow key={adv.id} data-testid={`advance-row-${adv.id}`}>
                      <TableCell className="font-medium">{adv.employee_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[adv.company] || COMPANY_COLORS.all}`}>
                          {getCompanyLabel(adv.company)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={adv.type === "loan" ? "bg-purple-100 text-purple-700" : "bg-amber-100 text-amber-700"}>
                          {adv.type === "loan" ? "Loan" : "Advance"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(adv.amount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(adv.monthly_emi)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600">{formatCurrency(adv.amount - adv.remaining_balance)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-red-600">{formatCurrency(adv.remaining_balance)}</TableCell>
                      <TableCell className="text-center text-xs text-slate-500">{adv.issued_date}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          {canEdit && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleOpenRecoveryDialog(adv)} data-testid={`recover-btn-${adv.id}`}>
                                <ArrowDownRight className="w-3 h-3 mr-1" />
                                Recover
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteAdvance(adv.id)} data-testid={`delete-advance-${adv.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-3 p-3 bg-amber-50 rounded-lg flex justify-between items-center">
              <span className="text-sm font-medium text-amber-800">Total Outstanding</span>
              <span className="text-lg font-bold text-amber-700 tabular-nums">{formatCurrency(advances.reduce((s, a) => s + a.remaining_balance, 0))}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-slate-500">{filteredExpenses.length} expenses in master list</p>
            <div className="flex flex-wrap gap-2">
              <Select value={filterCompany} onValueChange={setFilterCompany}>
                <SelectTrigger className="w-40" data-testid="filter-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANIES.map((company) => (
                    <SelectItem key={company.value} value={company.value}>
                      {company.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      {filteredExpenses.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Expense Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net Amount</TableHead>
                  <TableHead className="text-right w-44">Pay Amount</TableHead>
                  <TableHead className="text-right">Paid So Far</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center w-32">Action</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedExpenses.map(([categoryKey, categoryExpenses]) => {
                  const catLabel = categories.find(c => c.value === categoryKey)?.label || categoryKey;
                  const catColor = categoryColors[categoryKey] || categoryColors.other;
                  const isCollapsed = collapsedCategories[categoryKey];
                  const catTotal = categoryExpenses.reduce((sum, exp) => sum + getAmount(exp), 0);
                  const catPaid = categoryExpenses.reduce((sum, exp) => {
                    const tx = getTransaction(exp.id);
                    return sum + ((tx?.status === "paid" || tx?.status === "partial" || tx?.paid_via_cc_bill) ? (tx?.amount_paid || 0) : 0);
                  }, 0);
                  return (
                    <React.Fragment key={categoryKey}>
                      <TableRow
                        className="bg-slate-50 hover:bg-slate-100 cursor-pointer border-t-2 border-slate-200"
                        onClick={() => toggleCategory(categoryKey)}
                        data-testid={`category-group-${categoryKey}`}
                      >
                        <TableCell colSpan={3} className="py-2.5">
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${catColor}`}>
                              {catLabel}
                            </span>
                            <span className="text-xs text-slate-500">({categoryExpenses.length})</span>
                          </div>
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-slate-700 py-2.5">
                          {formatCurrency(catTotal)}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-emerald-600 py-2.5">
                          {formatCurrency(catPaid)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-amber-600 py-2.5">
                          {formatCurrency(catTotal - catPaid)}
                        </TableCell>
                        <TableCell colSpan={3}></TableCell>
                      </TableRow>
                      {!isCollapsed && categoryExpenses.map((expense) => {
                  const transaction = getTransaction(expense.id);
                  const editedValue = transaction ? editedAmounts[transaction.id] : undefined;
                  const allocatedAmount = getAmount(expense);
                  // For pending/draft, nothing has been paid yet. For partial/paid, use amount_paid.
                  const paidSoFar = (transaction?.status === "paid" || transaction?.status === "partial" || transaction?.paid_via_cc_bill) ? (transaction?.amount_paid || 0) : 0;
                  const balance = allocatedAmount - paidSoFar;
                  const hasChanged = transaction && editedValue !== undefined && parseNumericValue(editedValue) !== transaction.standard_amount;
                  const isPaid = transaction?.status === "paid";
                  const isPartial = transaction?.status === "partial";
                  const isPending = transaction?.status === "pending" || transaction?.status === "draft";
                  const isFullyPaid = isPaid || balance <= 0;
                  const isPaidViaCCBill = transaction?.paid_via_cc_bill === true;
                  
                  return (
                    <TableRow 
                      key={expense.id} 
                      data-testid={`expense-row-${expense.id}`}
                      className={isFullyPaid ? "bg-emerald-50/50" : ""}
                    >
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[expense.company] || COMPANY_COLORS.all}`}>
                          {getCompanyLabel(expense.company)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{expense.name}</p>
                          {expense.employee_name && (
                            <p className="text-xs text-slate-500">{expense.employee_name}</p>
                          )}
                          {expense.card_name && (
                            <p className="text-xs text-slate-500">{expense.card_name}</p>
                          )}
                          {(() => {
                            const adv = getActiveAdvance(expense.id);
                            if (adv) return (
                              <div className="mt-1 flex items-center gap-1">
                                <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">
                                  {adv.type === "loan" ? "Loan" : "Adv"}: {formatCurrency(adv.remaining_balance)} left
                                </Badge>
                              </div>
                            );
                            if (expense.category === "salary" && canEdit) return (
                              <button
                                className="mt-1 text-[10px] text-purple-600 hover:text-purple-800 flex items-center gap-0.5"
                                onClick={(e) => { e.stopPropagation(); handleOpenAdvanceDialog(expense); }}
                                data-testid={`issue-advance-${expense.id}`}
                              >
                                <HandCoins className="w-3 h-3" /> Issue Advance/Loan
                              </button>
                            );
                            return null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[expense.category] || categoryColors.other}`}>
                          {categories.find(c => c.value === expense.category)?.label || expense.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {formatCurrency(expense.base_amount || allocatedAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(() => {
                          const tdsRate = expense.tds_rate || 0;
                          const base = expense.base_amount || allocatedAmount;
                          const tdsAmt = Math.round(base * tdsRate / 100 * 100) / 100;
                          const isTdsPaid = tdsPaidMonths.has(selectedMonth);
                          return tdsAmt > 0 ? (
                            <div className={isTdsPaid ? "line-through text-red-500" : "text-emerald-600"}>
                              -{formatCurrency(tdsAmt)}
                              <span className={`text-xs block ${isTdsPaid ? "text-red-400" : "text-emerald-400"}`}>
                                @{tdsRate}% {isTdsPaid && "· Paid"}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                        {formatCurrency(allocatedAmount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {transaction ? (
                          isFullyPaid ? (
                            <span className="font-bold text-emerald-700 tabular-nums text-lg">
                              {formatCurrency(transaction.amount_paid)}
                            </span>
                          ) : isPartial ? (
                            <div className="relative inline-flex">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={editedAmounts[transaction.id] !== undefined ? editedAmounts[transaction.id] : formatIndianNumber(balance)}
                                onChange={(e) => handleAmountChange(transaction.id, e.target.value)}
                                onBlur={(e) => {
                                  const val = parseNumericValue(e.target.value);
                                  if (val > 0) {
                                    setEditedAmounts(prev => ({...prev, [transaction.id]: formatIndianNumber(val)}));
                                  }
                                }}
                                className="text-right pr-3 pl-7 w-40 font-medium border-amber-400 bg-amber-50/50"
                                data-testid={`amount-input-partial-${expense.id}`}
                              />
                            </div>
                          ) : (
                            <div className="relative inline-flex">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={editedAmounts[transaction.id] !== undefined ? editedAmounts[transaction.id] : formatIndianNumber(allocatedAmount)}
                                onChange={(e) => handleAmountChange(transaction.id, e.target.value)}
                                onBlur={(e) => {
                                  const val = parseNumericValue(e.target.value);
                                  if (val > 0) {
                                    setEditedAmounts(prev => ({...prev, [transaction.id]: formatIndianNumber(val)}));
                                  }
                                }}
                                className={`text-right pr-3 pl-7 w-40 font-medium ${hasChanged ? 'border-brand-orange bg-brand-orange/5' : ''}`}
                                data-testid={`amount-input-${expense.id}`}
                              />
                            </div>
                          )
                        ) : (
                          <div className="relative inline-flex">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={editedExpenseAmounts[expense.id] !== undefined ? editedExpenseAmounts[expense.id] : formatIndianNumber(allocatedAmount)}
                              onChange={(e) => handleExpenseAmountChange(expense.id, e.target.value)}
                              onBlur={(e) => {
                                const val = parseNumericValue(e.target.value);
                                if (val > 0) {
                                  setEditedExpenseAmounts(prev => ({...prev, [expense.id]: formatIndianNumber(val)}));
                                }
                              }}
                              className="text-right pr-3 pl-7 w-40 font-medium"
                              placeholder={formatIndianNumber(allocatedAmount)}
                              data-testid={`amount-input-new-${expense.id}`}
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={paidSoFar > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                          {formatCurrency(paidSoFar)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {isFullyPaid && balance > 0 ? (
                          <span className="text-blue-600 font-semibold" title="Savings: paid less than allocated">
                            Saved {formatCurrency(balance)}
                          </span>
                        ) : isFullyPaid && balance < 0 ? (
                          <span className="text-red-500 font-semibold" title="Over-spend: paid more than allocated">
                            Extra {formatCurrency(Math.abs(balance))}
                          </span>
                        ) : (
                          <span className={balance > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                            {formatCurrency(balance)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isPaidViaCCBill ? (
                          <Badge className="text-sm px-3 py-1 bg-blue-100 text-blue-700">
                            Paid via CC Bill
                          </Badge>
                        ) : isFullyPaid ? (
                          <Badge className="text-sm px-3 py-1 bg-emerald-100 text-emerald-700">
                            Paid
                          </Badge>
                        ) : isPartial ? (
                          <Badge className="text-sm px-3 py-1 bg-amber-100 text-amber-700" data-testid={`partial-badge-${expense.id}`}>
                            Partial ({formatCurrency(paidSoFar)})
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-sm px-3 py-1 bg-slate-100 text-slate-600">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {/* Paid via CC Bill - no manual action needed */}
                        {isPaidViaCCBill ? (
                          <span className="text-xs text-blue-600 font-medium">Auto-synced</span>
                        ) : isFullyPaid ? (
                          <Button
                            onClick={() => handleRevertToPending(transaction)}
                            disabled={savingId === transaction.id || savingId === expense.id}
                            size="sm"
                            variant="outline"
                            className="h-9 px-4 border-slate-300 hover:bg-slate-100"
                            data-testid={`revert-btn-${expense.id}`}
                          >
                            {savingId === transaction.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Undo2 className="w-4 h-4 mr-1" />
                                Undo
                              </>
                            )}
                          </Button>
                        ) : isPartial ? (
                          <div className="flex gap-1 justify-center">
                            <Button
                              onClick={() => handleMarkPaid(transaction)}
                              disabled={savingId === transaction.id}
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-3"
                              data-testid={`pay-more-btn-${expense.id}`}
                            >
                              {savingId === transaction.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <HandCoins className="w-4 h-4 mr-1" />
                                  Pay More
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleRevertToPending(transaction)}
                              disabled={savingId === transaction.id}
                              size="sm"
                              variant="outline"
                              className="h-9 px-2 border-slate-300 hover:bg-slate-100"
                              data-testid={`revert-partial-btn-${expense.id}`}
                            >
                              <Undo2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : transaction ? (
                          <Button
                            onClick={() => handleMarkPaid(transaction)}
                            disabled={savingId === transaction.id || savingId === expense.id}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4"
                            data-testid={`mark-paid-btn-${expense.id}`}
                          >
                            {savingId === transaction.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Mark Paid
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleMarkPaidWithoutTransaction(expense)}
                            disabled={savingId === expense.id}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4"
                            data-testid={`mark-paid-btn-${expense.id}`}
                          >
                            {savingId === expense.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Mark Paid
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDialog(expense)}
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Totals Row */}
          <div className="border-t border-slate-200 p-4 bg-slate-50">
            <div className="flex justify-end gap-8">
              <div className="text-right">
                <p className="text-xs text-slate-500">Total Allocated</p>
                <p className="text-lg font-bold text-slate-700 tabular-nums">{formatCurrency(totalAllocated)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Paid So Far</p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">{formatCurrency(totalPaidSoFar)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Balance Pending</p>
                <p className={`text-lg font-bold tabular-nums ${totalBalancePending > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {formatCurrency(totalBalancePending)}
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <Repeat className="empty-state-icon" />
              <h3 className="empty-state-title">No fixed expenses</h3>
              <p className="empty-state-text">
                Add recurring expenses like salaries, rent, utilities, etc.
              </p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="fixed-expense-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedExpense ? "Edit Fixed Expense" : "Add Fixed Expense"}
            </DialogTitle>
            <DialogDescription>
              {selectedExpense 
                ? `Changes to the amount will apply from ${selectedMonthLabel} onwards. Past months keep the old amount.`
                : "Fill in the details to add a recurring expense."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company}
                onValueChange={(value) => setFormData({ ...formData, company: value })}
              >
                <SelectTrigger data-testid="select-expense-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((company) => (
                    <SelectItem key={company.value} value={company.value}>
                      {company.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Expense Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Office Rent, John Doe Salary"
                required
                data-testid="input-expense-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value, other_category: value !== "other" ? "" : formData.other_category })}
                >
                  <SelectTrigger data-testid="select-expense-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                >
                  <SelectTrigger data-testid="select-expense-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.category === "salary" && (
              <div>
                <Label htmlFor="employee_name">Employee Name</Label>
                <Input
                  id="employee_name"
                  value={formData.employee_name}
                  onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                  placeholder="Enter employee name"
                  data-testid="input-employee-name"
                />
              </div>
            )}

            {formData.category === "credit-card" && (
              <div>
                <Label htmlFor="card_name">Card Name</Label>
                <Input
                  id="card_name"
                  value={formData.card_name}
                  onChange={(e) => setFormData({ ...formData, card_name: e.target.value })}
                  placeholder="e.g., HDFC Visa, ICICI MasterCard"
                  data-testid="input-card-name"
                />
              </div>
            )}

            {formData.category === "other" && (
              <div>
                <Label htmlFor="other_category">Specify "Other" Category</Label>
                <Input
                  id="other_category"
                  value={formData.other_category}
                  onChange={(e) => setFormData({ ...formData, other_category: e.target.value })}
                  placeholder="What is included in this expense?"
                  required
                  data-testid="input-other-category"
                />
              </div>
            )}

            <div>
              <Label htmlFor="base_amount">Base Amount (₹)</Label>
              <Input
                id="base_amount"
                type="text"
                inputMode="decimal"
                value={formData.base_amount}
                onChange={(e) => {
                  const val = handleNumericInput(e.target.value);
                  setFormData({ ...formData, base_amount: val });
                }}
                placeholder="Enter base amount"
                required
                data-testid="input-expense-amount"
              />
            </div>

            <GSTInput
              cgst_rate={formData.cgst_rate}
              sgst_rate={formData.sgst_rate}
              igst_rate={formData.igst_rate}
              onChange={(gstUpdate) => setFormData({ ...formData, ...gstUpdate })}
              handleNumericInput={handleNumericInput}
            />

            <div>
              <Label htmlFor="tds_rate">TDS Rate (%)</Label>
              <Input
                id="tds_rate"
                type="text"
                inputMode="decimal"
                value={formData.tds_rate}
                onChange={(e) => setFormData({ ...formData, tds_rate: handleNumericInput(e.target.value) })}
                placeholder="0"
                data-testid="input-tds-rate"
              />
            </div>

            {/* Live calculation preview */}
            {(() => {
              const base = parseNumericValue(formData.base_amount || "0");
              const gst = base * (parseNumericValue(formData.cgst_rate || "0") + parseNumericValue(formData.sgst_rate || "0") + parseNumericValue(formData.igst_rate || "0")) / 100;
              const tds = base * parseNumericValue(formData.tds_rate || "0") / 100;
              const net = base + gst - tds;
              return base > 0 ? (
                <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1" data-testid="calc-preview">
                  <div className="flex justify-between"><span className="text-slate-500">Base</span><span className="tabular-nums">{formatCurrency(base)}</span></div>
                  {gst > 0 && <div className="flex justify-between text-emerald-600"><span>+ GST</span><span className="tabular-nums">{formatCurrency(gst)}</span></div>}
                  {tds > 0 && <div className="flex justify-between text-red-500"><span>- TDS</span><span className="tabular-nums">{formatCurrency(tds)}</span></div>}
                  <div className="flex justify-between font-bold border-t pt-1"><span>Net Payable</span><span className="tabular-nums">{formatCurrency(net)}</span></div>
                </div>
              ) : null;
            })()}

            {formData.category !== "other" && (
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                  data-testid="input-expense-description"
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
                data-testid="save-fixed-expense"
              >
                {selectedExpense ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Fixed Expense</AlertDialogTitle>
            <AlertDialogDescription>
              "{selectedExpense?.name}" will stop appearing from <strong>{selectedMonthLabel}</strong> onwards. It will still be visible in previous months.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-expense"
            >
              Deactivate from {selectedMonthLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Project Allocation Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(open) => { if (!open) setPayDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="fixed-pay-project-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Expense</span><span className="font-medium">{payDialog.item.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Allocated</span><span className="tabular-nums">{formatCurrency(payDialog.item.standard_amount || payDialog.item.amount || 0)}</span></div>
                {(payDialog.alreadyPaid > 0) && (
                  <div className="flex justify-between text-emerald-600"><span>Already Paid</span><span className="tabular-nums">{formatCurrency(payDialog.alreadyPaid)}</span></div>
                )}
                {payDialog.advance && (
                  <div className="flex justify-between text-purple-600"><span>- {payDialog.advance.type === "loan" ? "Loan" : "Advance"} EMI</span><span className="tabular-nums">{formatCurrency(payDialog.advance.monthly_emi)}</span></div>
                )}
              </div>

              <div>
                <Label className="text-sm">Amount to Pay Now</Label>
                <Input
                  type="number"
                  value={payDialog.amount}
                  onChange={(e) => setPayDialog({ ...payDialog, amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 tabular-nums"
                  data-testid="fixed-pay-amount-input"
                />
              </div>

              <div>
                <Label className="text-sm">Bank Account</Label>
                <Select value={payDialogBankId || "none"} onValueChange={(v) => setPayDialogBankId(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="fixed-pay-bank-select">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select bank account</SelectItem>
                    {bankAccounts.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name} ({getCompanyLabel(b.company)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ProjectAllocation
                allocations={payDialogAllocations}
                totalAmount={payDialog.amount}
                onChange={setPayDialogAllocations}
              />

              <div>
                <Label className="text-sm">Payment Date</Label>
                <DatePicker value={payDate} onChange={(v) => setPayDate(v)} data-testid="fixed-pay-date-picker" />
              </div>

              {(() => {
                const allocated = payDialog.item.standard_amount || payDialog.item.amount || 0;
                const totalAfterPay = (payDialog.alreadyPaid || 0) + payDialog.amount;
                const diff = allocated - totalAfterPay;
                if (payDialog.amount > 0 && diff !== 0) {
                  return (
                    <div className={`p-2 rounded-lg text-xs font-medium flex justify-between ${diff > 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                      <span>{diff > 0 ? 'Remaining after this payment' : 'Over-spend vs allocated'}</span>
                      <span className="tabular-nums">{formatCurrency(Math.abs(diff))}</span>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPayDialog(null); setPayDate(""); }}>Cancel</Button>
                <Button 
                  onClick={() => handleConfirmFixedPayment(false)} 
                  disabled={savingId === payDialog.item.id} 
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50" 
                  data-testid="pay-partial-btn"
                >
                  {savingId === payDialog.item.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Pay Partial"}
                </Button>
                <Button 
                  onClick={() => handleConfirmFixedPayment(true)} 
                  disabled={savingId === payDialog.item.id} 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" 
                  data-testid="pay-and-close-btn"
                >
                  {savingId === payDialog.item.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Pay & Close"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Issue Advance/Loan Dialog */}
      <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
        <DialogContent className="max-w-md" data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle>Issue Advance / Loan</DialogTitle>
            <DialogDescription>
              Issue an advance or loan to {advanceForm.employee_name}. The amount will be deducted from bank and recovered from future salary payments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Type</Label>
                <Select value={advanceForm.type} onValueChange={(v) => setAdvanceForm(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="advance-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="loan">Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Amount (₹)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={advanceForm.amount}
                  onChange={(e) => setAdvanceForm(prev => ({ ...prev, amount: handleNumericInput(e.target.value) }))}
                  placeholder="e.g. 1,00,000"
                  className="mt-1"
                  data-testid="advance-amount-input"
                />
              </div>
            </div>
            {advanceForm.type === "loan" && (
              <div>
                <Label className="text-sm">Monthly EMI (₹)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={advanceForm.monthly_emi}
                  onChange={(e) => setAdvanceForm(prev => ({ ...prev, monthly_emi: handleNumericInput(e.target.value) }))}
                  placeholder="Monthly recovery amount"
                  className="mt-1"
                  data-testid="advance-emi-input"
                />
                {parseNumericValue(advanceForm.amount) > 0 && parseNumericValue(advanceForm.monthly_emi) > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Recovery in ~{Math.ceil(parseNumericValue(advanceForm.amount) / parseNumericValue(advanceForm.monthly_emi))} months
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-sm">Bank Account (to disburse from)</Label>
              <Select value={advanceForm.bank_account_id || "none"} onValueChange={(v) => setAdvanceForm(prev => ({ ...prev, bank_account_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="advance-bank-select">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select bank account</SelectItem>
                  {bankAccounts.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name} ({getCompanyLabel(b.company)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                value={advanceForm.notes}
                onChange={(e) => setAdvanceForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Reason for advance/loan"
                className="mt-1"
                data-testid="advance-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateAdvance} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-advance-btn">
                Issue {advanceForm.type === "loan" ? "Loan" : "Advance"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Dialog */}
      <Dialog open={!!recoveryDialog} onOpenChange={(open) => { if (!open) setRecoveryDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="recovery-dialog">
          <DialogHeader>
            <DialogTitle>Record Recovery</DialogTitle>
            <DialogDescription>
              Record a manual recovery from {recoveryDialog?.employee_name} for {selectedMonthLabel}.
            </DialogDescription>
          </DialogHeader>
          {recoveryDialog && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-purple-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="font-bold text-purple-700 tabular-nums">{formatCurrency(recoveryDialog.remaining_balance)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Suggested EMI</span><span className="tabular-nums">{formatCurrency(recoveryDialog.monthly_emi)}</span></div>
              </div>
              <div>
                <Label className="text-sm">Recovery Amount (₹)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={recoveryAmount}
                  onChange={(e) => setRecoveryAmount(handleNumericInput(e.target.value))}
                  className="mt-1"
                  data-testid="recovery-amount-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRecoveryDialog(null)}>Cancel</Button>
                <Button onClick={handleRecordRecovery} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-recovery-btn">
                  Record Recovery
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="fixed-expenses"
        title="Fixed Expenses"
        showDateFilter={false}
        showCompanyFilter={true}
      />
    </div>
  );
}
